import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
  Input,
} from '@/shared/components';
import { type EditCardFormValues } from '../model';
import { useTranslation } from 'react-i18next';
import type { UseFormReturn } from 'react-hook-form';
import { createRegexInputHandlers, getRequiredLabel } from '@/shared/lib';
import {
  type CardPaymentSystemConfig,
  getCardPaymentSystem,
} from '@/entities/card';

type Props = {
  form: UseFormReturn<EditCardFormValues>;
  paymentSystems: CardPaymentSystemConfig;
  updateCard: () => Promise<void>;
  deleteCard: () => Promise<void>;
  isPending: boolean;
};

export const EditCardForm = ({
  form,
  paymentSystems,
  updateCard,
  isPending,
  deleteCard,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Form {...form}>
      <form
        className="flex flex-col gap-[1.6rem]"
        onSubmit={form.handleSubmit(updateCard)}>
        <FormField
          control={form.control}
          name="cardNumber"
          render={({ field }) => {
            const system = getCardPaymentSystem(field.value, paymentSystems);

            const icon = system?.icon ? (
              <img
                src={system.icon}
                alt={system.name ?? 'Card'}
                className="w-[2.4rem] h-[2.4rem] object-contain"
              />
            ) : (
              <div className="w-[1.8rem] h-[1.2rem] bg-tertiary rounded-[0.3rem]" />
            );

            return (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    disabled
                    className="p3"
                    label={getRequiredLabel(
                      t('withdrawMethods.card.cardNumber')
                    )}
                    leftContent={
                      <div className="size-[2.4rem] mr-[0.8rem] flex items-center justify-center mt-[1rem]">
                        {icon}
                      </div>
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="cardholderName"
          render={({ field }) => {
            const handlers = createRegexInputHandlers(/^[A-Za-z ]$/, value => {
              field.onChange(value.toUpperCase());
            });

            return (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    type="text"
                    className="p3"
                    label={getRequiredLabel(
                      t('withdrawMethods.card.cardHolderName')
                    )}
                    onBeforeInput={handlers.beforeInput}
                    onPaste={handlers.paste}
                    onChange={handlers.change}
                    onBlur={e => {
                      field.onChange(e.currentTarget.value.trim());
                      field.onBlur();
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          control={form.control}
          name="templateName"
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Input
                    {...field}
                    label={getRequiredLabel(
                      t('savedAddresses.bankAddress.fields.templateNameLabel')
                    )}
                    className="p3"
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage className="px-[1.6rem]" />
              </FormItem>
            );
          }}
        />

        <Button
          size="lg"
          type="submit"
          loading={isPending}
          className="mt-[1.6rem]">
          {t('savedAddresses.card.editing.buttonText')}
        </Button>

        <Button
          size="lg"
          type="button"
          variant="secondary"
          onClick={deleteCard}
          disabled={isPending}>
          {t('savedAddresses.card.deletion.buttonText')}
        </Button>
      </form>
    </Form>
  );
};
